import angular from "angular";

import { Dates, Invoice, InvoiceListItem, InvoicesLogic, InvoiceStatusNotification, PaymentChannel, PaymentChannelApi, Translations } from "@salaxy/core";

import { AjaxNg1 } from "../../ajax";
import { SessionService, UiHelpers } from "../../services";
import { ApiCrudObjectController } from "../bases";

/**
 * Provides access for a payment channel to access invoices
 * that were sent to that channel and also the calculations etc. related to those invoices.
 * Only a Partner with registered Payment Channel can use this Controller.
 * Please contact Palkkaus.fi support for more information.
 */
export class PaymentChannelController extends ApiCrudObjectController<Invoice, InvoiceListItem> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "AjaxNg1",
    "UiHelpers",
    "$location",
    "$routeParams",
    "SessionService",
    "$scope",
  ];

  /** Status notification data for UI binding to update status. */
  public statusData: InvoiceStatusNotification;

  private _channel: PaymentChannel;

  private _initialOwner: string;

  private unread: Invoice[];

  constructor(
    private ajax: AjaxNg1,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private sessionService: SessionService,
    private $scope: angular.IScope,
  ) { // Dependency injection
    super(new PaymentChannelApi(null, ajax), uiHelpers, $location, $routeParams);
  }

  /**
   * Initialization code.
   */
  public $onInit() {
    const setChannel = () => {

      if (this.sessionService.isSessionChecked && this.sessionService.isAuthenticated) {
        const owner = this.sessionService.session.currentAccount.owner;
        if (this._initialOwner != owner){
          this._initialOwner = owner;
          this.channel = this.sessionService.getOwnedPaymentChannel();
          this.reload();
          this.unread = null;
          if (this.odataController) {
            this.odataController.refreshClearAll();
          }
        }
      } else {
        this._initialOwner = null;
        this.channel = null;
        this.api = null;
        this.unread = null;
          if (this.odataController) {
            this.odataController.refreshClearAll();
          }
      }
    };

    setChannel();

    this.sessionService.subscribe(this.$scope, () => {
      setChannel();
    });

    super.$onInit();
  }

  /** Name/key of the channel on which the controller operates. */
  public get channel(): PaymentChannel {
    return this._channel;
  }
  public set channel(value: PaymentChannel) {
    this._channel = value;
    if (value) {
      this.api = new PaymentChannelApi(value, this.ajax);
    }
  }

  /** Gets the full items for unread objects. */
  public get unreadInvoices(): Invoice[] {
    if (!this.unread) {
      this.unread = [];
      this.paymentChannelApi.getInvoicesByStatus().then((items) => {
        this.unread = items;
      });
    }
    return this.unread;
  }


  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "payment-channel",
      detailsUrl: this.detailsUrl || "/payment-channel/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/InvoicesPanel.html",
      oDataOptions: {},
    };
  }

  /** Returns content url for the invoice. */
  public getContentUrl(id: string, inline = false): string {
    return this.paymentChannelApi.getContentUrl(id, inline);
  }

  /**
   * Updates the status of the invoice.
   * @param data Status data. If not set, uses the statusData property of the controller.
   */
  public updateStatus(data: InvoiceStatusNotification = null): Promise<Invoice> {
    const loading = this.uiHelpers.showLoading();
    data = data || this.statusData;
    return this.setStatus(this.paymentChannelApi.updateStatus([data])
      .then((invoices) => {
        loading.dismiss();
        this.setCurrent(invoices[0]);
        this.resetStatusData();
        return invoices[0];
      }));
  }

  /** Resets the status data according to current Invoice. */
  public resetStatusData() {
    if (!this.current) {
      this.statusData = null;
      return;
    }
    this.statusData = {
      id: this.current.id,
      externalId: this.current.header.externalId,
      date: Dates.getToday(),
      status: this.current.header.status,
      amount: this.current.header.total,
    };
  }

  /** Gets and avatar image for a payment channel */
  public getChannelAvatar() {
    return InvoicesLogic.getChannelAvatar(this.channel);
  }

  /** Data reader for selected items. */
  public selectionDataReader = {
    /** Function to read data into given array */
    read: (arr: Invoice[], message: string): Promise<void> => { // eslint-disable-line @typescript-eslint/no-unused-vars
      const promises = this.odataController.selectedItems.map(x => this.api.getSingle(x.id));
      return Promise.all(promises).then((data) => {
        arr.push(...data.filter(d => !!d));
        return;
      });
    },
    /** Optional label for data export */
    exportLabel: Translations.get("SALAXY.NG1.AccountingReportToolsComponent.exportLabel"),
    /** Indicates if the export is not possible */
    disabled: () => !this.odataController.selectedItems || this.odataController.selectedItems.length === 0,
  };

  /**
   * Data reader for one item.
   * TODO: Should add interface for the reader.
   */
  public getItemDataReader = (item: {
    /** List item id */
    id: string,
    /** List item owner */
    owner: string,
  }) => {
    return {
      /** Function to read data into given array */
      read: (arr: Invoice[], message: string): Promise<void> => { // eslint-disable-line @typescript-eslint/no-unused-vars
        (item as any).isReading = true;
        return this.api.getSingle(item.id).then((data) => {
          /*
          const wfEvent = {
            type: "PartnerMessageClosed",
            ui: "success" as any,
            message,
          };
          return this.filesApi.saveWorkflowEvent(item, wfEvent).then(() => {
            this.setWorkflowEvent(item as any, wfEvent);
          */
          if (data) {
            arr.push(data);
          }
          (item as any).isReading = false;
          return Promise.resolve();
          /* });*/
        });
      },
      /** Optional label for data export */
      exportLabel: null,
      /** Indicates if the export is not possible */
      disabled: () => false,
    };
  };

  /** Unselect all */
  public unselectAll() {
    if (this.odataController.selectedItems) {
      this.odataController.selectedItems.splice(0, this.odataController.selectedItems.length);
    }
  }

  /** Select all */
  public selectAll() {
    if (this.odataController.selectedItems && this.odataController.items) {
      this.odataController.selectedItems.splice(0, this.odataController.selectedItems.length);
      this.odataController.selectedItems.push(...this.odataController.items);
    }
  }

  /**
   * Updates the invoice. Only valid in the test environment.
   */
  public save(): Promise<Invoice> {
    const loading = this.uiHelpers.showLoading();
    return this.ajax.postJSON( `/v03/api/payment-channel/${this.channel}`, this.current)
      .then((invoice) => {
        loading.dismiss();
        this.setCurrent(invoice);
        return invoice;
      });
  }

  private get paymentChannelApi() {
    return this.api as PaymentChannelApi;
  }
}
